import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/authContext';
import { useWalkthroughChef } from '../context/WalkthroughChefContext';
import WalkthroughModal from './WalkthroughModal';
import WalkthroughOverlay from './WalkthroughOverlay';
import { trackLineageWalkthroughComplete } from '../services/api';

const LineageWalkthroughManager = ({ isActive, onComplete }) => {
    const { user } = useAuth();
    const { setWalkthroughChef, clearWalkthroughChef } = useWalkthroughChef();
    const navigate = useNavigate();
    const location = useLocation();
    const [internalIsActive, setInternalIsActive] = useState(isActive);
    const [currentStep, setCurrentStep] = useState(0);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [highlightedElement, setHighlightedElement] = useState(null);
    const [overlayMessage, setOverlayMessage] = useState('');
    const [overlaySubMessage, setOverlaySubMessage] = useState('');
    const [chefState, setChefState] = useState('talking');
    const [displayText, setDisplayText] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const textIntervalRef = useRef(null);
    const hasInitializedRef = useRef(false);
    const stepStartPathnameRef = useRef(null);
    const lastProcessedStepRef = useRef(-1);
    
    // Explicit fork completion state
    const [forkCompleted, setForkCompleted] = useState(false);
    const forkCompletedRef = useRef(false);

    // Step 1: Check if component persists across navigation
    useEffect(() => {
        // Component mounted/unmounted tracking removed
    }, []);

    // Sync internal isActive with prop (but allow it to be set independently for resume)
    useEffect(() => {
        if (isActive) {
            setInternalIsActive(true);
        } else {
            // Only set to false if we're not expecting a resume
            // Check if there's a pending resume in localStorage
            try {
                const payloadRaw = window.localStorage.getItem('lineageWalkthroughNextStep');
                if (payloadRaw) {
                    const payload = JSON.parse(payloadRaw);
                    const isRecent = !payload.createdAt || Date.now() - payload.createdAt < 60_000;
                    // If there's a recent pending resume, don't deactivate yet
                    if (isRecent) {
                        return;
                    }
                }
            } catch (e) {
                // If we can't check, proceed with deactivation
            }
            // Only deactivate if no resume is pending
            setInternalIsActive(false);
        }
    }, [isActive]);

    // Step 2: Resume walkthrough from localStorage on mount/pathname change
    // This is the PRIMARY mechanism for resuming after navigation
    // MUST run synchronously on mount to prevent cleanup from clearing state
    useEffect(() => {
        const pathname = location.pathname;

        // Check IMMEDIATELY (no delay) to prevent cleanup from running first
        let payloadRaw: string | null = null;
        try {
            payloadRaw = window.localStorage.getItem('lineageWalkthroughNextStep');
        } catch (e) {
            return;
        }

        if (!payloadRaw) {
            return;
        }

        let payload: {
            nextStep: number;
            fromPath?: string | null;
            toPath?: string | null;
            createdAt?: number;
        };

        try {
            payload = JSON.parse(payloadRaw);
        } catch {
            window.localStorage.removeItem('lineageWalkthroughNextStep');
            return;
        }

        const isRecent = !payload.createdAt || Date.now() - payload.createdAt < 60_000;
        // More flexible path matching - check if we're on any recipe page if toPath is a recipe page
        const isCorrectDest = !payload.toPath || 
                             payload.toPath === pathname ||
                             (payload.toPath.startsWith('/recipes/') && pathname.startsWith('/recipes/') && pathname !== '/recipes');

        if (!isRecent || !isCorrectDest) {
            // stale or wrong page, clear and bail
            window.localStorage.removeItem('lineageWalkthroughNextStep');
            return;
        }

        if (payload.nextStep === 6) {
            // Remove payload immediately to prevent duplicate resumes
            window.localStorage.removeItem('lineageWalkthroughNextStep');

            // Force step 6 modal here - do this synchronously
            setInternalIsActive(true);
            setCurrentStep(6);
            setIsModalOpen(true);
            
            // Mark as initialized to prevent reset
            hasInitializedRef.current = true;

            // Clear any leftover highlights / overlays
            setHighlightedElement(null);
            setOverlayMessage('');
            setOverlaySubMessage('');
            clearWalkthroughChef();
            
            // Mark fork as completed
            forkCompletedRef.current = true;
            setForkCompleted(true);
        }
    }, [location.pathname, clearWalkthroughChef]);

    const handleComplete = useCallback(async () => {
        // Deactivate walkthrough immediately to prevent restart
        setInternalIsActive(false);
        setIsModalOpen(false);
        setHighlightedElement(null);
        setOverlayMessage('');
        setOverlaySubMessage('');
        clearWalkthroughChef(); // Clear walkthrough chef
        
        // Clean up all disabled/grayed-out tabs
        const allTabs = document.querySelectorAll('.walkthrough-disabled, .walkthrough-grayed-out');
        allTabs.forEach(tab => {
            tab.classList.remove('walkthrough-disabled', 'walkthrough-grayed-out');
            tab.style.opacity = '';
            tab.style.pointerEvents = '';
            tab.style.cursor = '';
        });
        
        // Reset state
        setCurrentStep(0);
        hasInitializedRef.current = false;
        forkCompletedRef.current = false;
        setForkCompleted(false);
        
        if (user) {
            try {
                const token = await user.getIdToken();
                await trackLineageWalkthroughComplete(token);
            } catch (error) {
                console.error('Error tracking walkthrough completion:', error);
            }
        }
        
        if (onComplete) {
            onComplete();
        }
    }, [user, onComplete, clearWalkthroughChef]);
    
    const handleSkip = useCallback(async () => {
        // Deactivate walkthrough immediately
        setInternalIsActive(false);
        setIsModalOpen(false);
        setHighlightedElement(null);
        setOverlayMessage('');
        setOverlaySubMessage('');
        clearWalkthroughChef();
        
        // Clean up all disabled/grayed-out tabs
        const allTabs = document.querySelectorAll('.walkthrough-disabled, .walkthrough-grayed-out');
        allTabs.forEach(tab => {
            tab.classList.remove('walkthrough-disabled', 'walkthrough-grayed-out');
            tab.style.opacity = '';
            tab.style.pointerEvents = '';
            tab.style.cursor = '';
        });
        
        // Reset state
        setCurrentStep(0);
        hasInitializedRef.current = false;
        forkCompletedRef.current = false;
        setForkCompleted(false);
        
        // Track skip (we'll use localStorage to track this, as there's no skip endpoint)
        // The skip will be tracked when the user starts the walkthrough again or on next page load
        try {
            localStorage.setItem('lineageWalkthroughSkipped', 'true');
            localStorage.setItem('lineageWalkthroughSkipTimestamp', Date.now().toString());
        } catch (error) {
            console.error('Error storing skip status:', error);
        }
        
        if (onComplete) {
            onComplete();
        }
    }, [onComplete, clearWalkthroughChef]);

    const markForkCompleted = useCallback(() => {
        if (forkCompletedRef.current) {
            return; // idempotent
        }
        forkCompletedRef.current = true;
        setForkCompleted(true);
    }, []);

    const handleStartCompareWalkthrough = useCallback(() => {
        // Complete the lineage walkthrough first
        handleComplete();
        // TODO: Start compare walkthrough here
        // This will be implemented when compare walkthrough is created
    }, [handleComplete]);

    const walkthroughSteps = [
        // Step 0: Welcome modal
        {
            type: 'modal',
            title: 'Lineage Walkthrough',
            message: "Hi! I'm Chef. Let me show you how to explore recipe lineages and create forks!",
            subMessage: "This walkthrough will guide you through the lineage system step by step.",
            chefState: 'talking',
            showStartButton: true,
            startButtonText: "Let's Start!"
        },
        // Step 1: Click lineage tab (only if not already on lineage page)
        {
            type: 'highlight',
            targetSelector: '[data-walkthrough="lineage-tab"]',
            message: "First, click the lineage tab to open the lineage viewer.",
            subMessage: "This shows you the family tree of recipes - how recipes branch and evolve.",
            chefState: 'talking',
            position: 'right',
            waitForPage: '/recipes',
            skipIfLineageOpen: true
        },
                // Step 2: Explain lineage page
                {
                    type: 'highlight',
                    targetSelector: '.lineage-background',
                    message: "This is the lineage viewer! It shows how recipes connect and branch.",
                    subMessage: "The root recipe is on the left, and forks extend to the right. You can drag to explore.",
                    chefState: 'talking',
                    position: 'bottom',
                    waitForInteraction: false, // Don't wait for click, use Next button instead
                    showNextButton: true
                },
        // Step 3: Click plus tab
        {
            type: 'highlight',
            targetSelector: '[data-walkthrough="fork-tab"]',
            message: "Now let's create a fork! Click the plus tab.",
            subMessage: "This will let you create a new version of this recipe with your own modifications.",
            chefState: 'talking',
            position: 'right',
            waitForInteraction: true,
            showNextButton: false
        },
        // Step 4: Change first ingredient
        {
            type: 'highlight',
            targetSelector: '[data-walkthrough="fork-ingredient-0"]',
            message: "Great! Now modify the first ingredient in the fork modal.",
            subMessage: "Change it to something different - this creates your unique fork of the recipe.",
            chefState: 'talking',
            position: 'left',
            waitForInteraction: false,
            interactionType: 'input',
            showNextButton: true
        },
        // Step 5: Click Create Fork button
        {
            type: 'highlight',
            targetSelector: '[data-walkthrough="fork-submit"]',
            message: "Perfect! Now click 'Create Fork' to save your changes.",
            subMessage: "This will create a new recipe in the lineage tree.",
            chefState: 'talking',
            position: 'top',
            waitForInteraction: true,
            showNextButton: false
        },
        // Step 6: Finish modal (shown after fork creation)
        {
            type: 'modal',
            title: 'Great Job!',
            message: "You've successfully created a fork! Your new recipe is now part of the lineage tree.",
            subMessage: "To compare recipes, click the compare tab and select two recipes from your lineage. You'll see side-by-side what changed between them!",
            chefState: 'fire',
            showStartButton: false,
            showImage: true,
            imageUrl: '/images/lineage-placeholder.png',
            showEndButton: true,
            endButtonText: "Got it!"
        }
    ];

    const handleNext = useCallback(() => {
        setCurrentStep(prevStep => {
            const nextStep = prevStep + 1;
            if (nextStep < walkthroughSteps.length) {
                return nextStep;
            } else {
                handleComplete();
                return prevStep;
            }
        });
    }, [currentStep, handleComplete, walkthroughSteps.length]);

    // Track skip on page reload/unload
    useEffect(() => {
        const handleBeforeUnload = () => {
            // If walkthrough is active, mark as skipped
            // BUT only if we're not expecting a resume (i.e., no pending nextStep)
            try {
                const payloadRaw = window.localStorage.getItem('lineageWalkthroughNextStep');
                if (payloadRaw) {
                    const payload = JSON.parse(payloadRaw);
                    const isRecent = !payload.createdAt || Date.now() - payload.createdAt < 60_000;
                    if (isRecent) {
                        // Resume is pending, don't mark as skipped
                        return;
                    }
                }
            } catch (e) {
                // If we can't check, proceed with skip tracking
            }
            
            if (internalIsActive && hasInitializedRef.current) {
                try {
                    localStorage.setItem('lineageWalkthroughSkipped', 'true');
                    localStorage.setItem('lineageWalkthroughSkipTimestamp', Date.now().toString());
                } catch (error) {
                    console.error('Error storing skip status on unload:', error);
                }
            }
        };
        
        window.addEventListener('beforeunload', handleBeforeUnload);
        
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [internalIsActive, hasInitializedRef.current]);

    // Reset when isActive becomes true
    // IMPORTANT: This effect runs AFTER resume effect, so cleanup must check for pending resume
    useEffect(() => {
        if (isActive && !hasInitializedRef.current) {
            // Reset everything when walkthrough starts
            setCurrentStep(0);
            setIsModalOpen(true); // Open modal immediately for step 0
            setHighlightedElement(null);
            setDisplayText(''); // Will be set by step processing effect
            setIsTyping(false);
            setOverlayMessage('');
            setOverlaySubMessage('');
            hasInitializedRef.current = true;
        } else if (!internalIsActive) {
            // CRITICAL: Check for pending resume BEFORE cleanup
            // This must happen synchronously to prevent race condition
            // ALWAYS check for pending resume BEFORE cleanup
            // This prevents cleanup from running when we're about to resume
            let shouldCleanup = true;
            try {
                const payloadRaw = window.localStorage.getItem('lineageWalkthroughNextStep');
                if (payloadRaw) {
                    const payload = JSON.parse(payloadRaw);
                    const isRecent = !payload.createdAt || Date.now() - payload.createdAt < 60_000;
                    const currentPath = location.pathname;
                    const isCorrectDest = !payload.toPath || 
                                         payload.toPath === currentPath ||
                                         (payload.toPath?.startsWith('/recipes/') && currentPath.startsWith('/recipes/') && currentPath !== '/recipes');
                    
                    if (isRecent && isCorrectDest) {
                        shouldCleanup = false;
                        // If we're about to resume, set internalIsActive to true to prevent further cleanup
                        if (payload.nextStep === 6) {
                            setInternalIsActive(true);
                            hasInitializedRef.current = true;
                        }
                    }
                }
            } catch (e) {
                // If we can't check, proceed with cleanup
            }
            
            if (shouldCleanup) {
                // Clean up when walkthrough is deactivated
                setCurrentStep(0);
                setIsModalOpen(false);
                setHighlightedElement(null);
                setDisplayText('');
                setIsTyping(false);
                setOverlayMessage('');
                setOverlaySubMessage('');
                clearWalkthroughChef(); // Clear walkthrough chef
                
                // Clean up all disabled/grayed-out tabs
                const allTabs = document.querySelectorAll('.walkthrough-disabled, .walkthrough-grayed-out');
                allTabs.forEach(tab => {
                    tab.classList.remove('walkthrough-disabled', 'walkthrough-grayed-out');
                    tab.style.opacity = '';
                    tab.style.pointerEvents = '';
                    tab.style.cursor = '';
                });
                
                hasInitializedRef.current = false;
                if (textIntervalRef.current) {
                    clearInterval(textIntervalRef.current);
                    textIntervalRef.current = null;
                }
                // Reset fork completion state
                setForkCompleted(false);
                forkCompletedRef.current = false;
            }
        }
    }, [internalIsActive, clearWalkthroughChef]);

    // Watch for fork creation completion (navigation after fork submit)
    useEffect(() => {
        if (!internalIsActive || !hasInitializedRef.current) return;
        
        const step = walkthroughSteps[currentStep];
        if (!step || step.type !== 'highlight') return;
        
        // If we're on step 5 (fork-submit), watch for navigation after fork creation
        if (step.targetSelector && step.targetSelector.includes('fork-submit')) {
            // Capture the pathname when step 5 starts (only once, using ref)
            if (stepStartPathnameRef.current === null) {
                stepStartPathnameRef.current = location.pathname;
            }
            
            let hasProgressed = false;
            
            const checkForkCreated = () => {
                if (hasProgressed) return;
                
                // Check for fork modal - look for the actual modal content elements
                // The modal container might persist in DOM, so check for the actual content
                const forkModalContent = document.querySelector('[data-walkthrough="fork-ingredient-0"]') ||
                                        document.querySelector('[data-walkthrough="fork-submit"]');
                // Also check if the modal container exists AND has visible content
                const forkModalContainer = Array.from(document.querySelectorAll('.fixed.inset-0')).find(modal => {
                    const zIndex = window.getComputedStyle(modal).zIndex;
                    const hasContent = modal.querySelector('[data-walkthrough="fork-ingredient-0"]') !== null ||
                                     modal.querySelector('[data-walkthrough="fork-submit"]') !== null;
                    return parseInt(zIndex) >= 99999 && hasContent;
                });
                // Modal is visible only if content exists and is actually visible
                const forkModalVisible = (forkModalContent && forkModalContent.offsetParent !== null) ||
                                        (forkModalContainer && forkModalContainer.offsetParent !== null);
                const isOnRecipePage = location.pathname.startsWith('/recipes/') && location.pathname !== '/recipes';
                const pathnameChanged = location.pathname !== stepStartPathnameRef.current;
                
                // If pathname changed AND we're on a recipe page, fork was created (navigation happened)
                if (pathnameChanged && isOnRecipePage) {
                    // Give it a moment to ensure navigation completed
                    setTimeout(() => {
                        if (hasProgressed) {
                            return;
                        }
                        
                        hasProgressed = true;
                        
                        // Store resume intent in localStorage FIRST (before any state changes)
                        // This ensures it's available even if component unmounts during navigation
                        const fromPath = stepStartPathnameRef.current;
                        const newRecipePath = location.pathname;
                        const payload = {
                            nextStep: 6,
                            fromPath: fromPath,
                            toPath: newRecipePath,
                            createdAt: Date.now(),
                        };
                        
                        try {
                            window.localStorage.setItem(
                                'lineageWalkthroughNextStep',
                                JSON.stringify(payload)
                            );
                        } catch (e) {
                            // Failed to write localStorage
                        }
                        
                        // Reset the ref for next time
                        stepStartPathnameRef.current = null;
                        
                        // Try to directly progress to step 6 (finish modal) immediately
                        // But also rely on resume effect as backup
                        
                        // Use a longer timeout to ensure navigation has completed
                        setTimeout(() => {
                            // Check if we're still on the correct page and component is still mounted
                            const currentPath = window.location.pathname;
                            if (currentPath === newRecipePath || currentPath.startsWith('/recipes/')) {
                                // Ensure walkthrough is active
                                setInternalIsActive(true);
                                
                                // Set step 6 and open modal
                                setCurrentStep(6);
                                setIsModalOpen(true);
                                
                                // Clear any highlighted elements from step 5 (fork-submit)
                                setHighlightedElement(null);
                                setOverlayMessage('');
                                setOverlaySubMessage('');
                                clearWalkthroughChef();
                                
                                // Mark fork as completed
                                forkCompletedRef.current = true;
                                setForkCompleted(true);
                                
                                // Mark as initialized so resume effect doesn't interfere
                                hasInitializedRef.current = true;
                            }
                        }, 1000); // Longer delay to ensure navigation completed
                    }, 500);
                }
            };
            
            // Check immediately and periodically
            checkForkCreated();
            const interval = setInterval(checkForkCreated, 200);
            
            return () => {
                clearInterval(interval);
            };
        } else {
            // Reset the ref when not on step 5
            stepStartPathnameRef.current = null;
        }
    }, [currentStep, isActive, hasInitializedRef.current, location.pathname, walkthroughSteps, handleComplete, markForkCompleted]);

    // Step 3: Dedicated effect for step 6 (finish modal) - only when fork is actually created
    // This handles the case where fork is created and we need to show the modal after navigation
    useEffect(() => {
        // Only run this when fork is actually completed (not when user clicks Next normally)
        if (!forkCompleted) return;
        if (currentStep !== 6) return; // Only run if we're already on step 6

        // Clear all highlight / overlay state
        setHighlightedElement(null);
        setOverlayMessage('');
        setOverlaySubMessage('');
        clearWalkthroughChef();

        // Ensure modal is open
        setIsModalOpen(true);
    }, [forkCompleted, currentStep, clearWalkthroughChef]);

    // Watch for modal opens to detect tab clicks and disable tabs
    useEffect(() => {
        if (!internalIsActive || !hasInitializedRef.current) return;
        
        const step = walkthroughSteps[currentStep];
        if (!step || step.type !== 'highlight') return;
        
        const targetSelector = step.targetSelector;
        if (!targetSelector) return;
        
        // Skip fork-submit step - it's handled by the fork creation watcher above
        if (targetSelector.includes('fork-submit')) {
            return;
        }
        
        // Check if this step targets a tab that opens a modal
        const isTabStep = targetSelector.includes('lineage-tab') || 
                         targetSelector.includes('fork-tab') || 
                         targetSelector.includes('compare-tab');
        
        if (isTabStep) {
            const target = document.querySelector(targetSelector);
            if (!target) {
                return;
            }
            
            let hasProgressed = false; // Prevent multiple progressions
            
            // Watch for the modal to open
            const checkModalOpen = () => {
                if (hasProgressed) return; // Already progressed, don't check again
                
                let modalOpen = false;
                
                if (targetSelector.includes('lineage-tab')) {
                    // Check multiple ways to detect if lineage modal is open:
                    // 1. Check if the tab shows X (close icon) - means modal is open
                    const tabContent = target.innerHTML || '';
                    const hasCloseIcon = tabContent.includes('✕');
                    
                    // 2. Check if LineageViewerV2 is rendered (look for lineage-background class)
                    const lineageBackground = document.querySelector('.lineage-background');
                    
                    // 3. Check if the tab's inner div shows X
                    const innerDiv = target.querySelector('div > div');
                    const innerHasX = innerDiv && innerDiv.innerHTML.includes('✕');
                    
                    // Only check for SVG if it's inside the lineage viewer area (more specific)
                    const lineageContainer = document.querySelector('.lineage-background');
                    const hasLineageSVG = lineageContainer && lineageContainer.querySelector('svg') !== null;
                    
                    // MOST RELIABLE: Only use X icon check - this is the primary indicator that modal is open
                    // The X icon appears when isModalOpen is true in FloatingLineageTab
                    modalOpen = hasCloseIcon || innerHasX;
                    
                    // Only use lineage background + SVG as backup if X icon check fails
                    // But prefer X icon because it's the most direct indicator
                    if (!modalOpen && lineageBackground !== null && hasLineageSVG) {
                        // Double-check: make sure the lineage background is actually visible
                        const isVisible = lineageBackground.offsetParent !== null;
                        if (isVisible) {
                            modalOpen = true;
                        }
                    }
                } else if (targetSelector.includes('fork-tab')) {
                    // Check for fork modal - look for actual modal content elements
                    // Also check if the tab shows X (close icon) - means modal is open
                    const tabContent = target.innerHTML || '';
                    const hasCloseIcon = tabContent.includes('✕');
                    const innerDiv = target.querySelector('div > div');
                    const innerHasX = innerDiv && innerDiv.innerHTML.includes('✕');
                    
                    // Check for actual fork modal content (most reliable)
                    const forkModalContent = document.querySelector('[data-walkthrough="fork-ingredient-0"]') ||
                                            document.querySelector('[data-walkthrough="fork-submit"]');
                    // Also check for modal container that contains the fork content
                    const forkModalContainer = Array.from(document.querySelectorAll('.fixed.inset-0')).find(modal => {
                        const zIndex = window.getComputedStyle(modal).zIndex;
                        const hasForkContent = modal.querySelector('[data-walkthrough="fork-ingredient-0"]') !== null ||
                                             modal.querySelector('[data-walkthrough="fork-submit"]') !== null;
                        return parseInt(zIndex) >= 99999 && hasForkContent;
                    });
                    
                    // Modal is open if content exists and is visible, OR if tab shows X icon
                    modalOpen = (forkModalContent && forkModalContent.offsetParent !== null) ||
                               (forkModalContainer && forkModalContainer.offsetParent !== null) ||
                               hasCloseIcon || innerHasX;
                } else if (targetSelector.includes('compare-tab')) {
                    // Check for compare selection modal
                    const compareModal = document.querySelector('.compare-selection-modal') ||
                                       document.querySelector('[data-walkthrough="compare-selection"]');
                    modalOpen = compareModal !== null;
                }
                
                if (modalOpen && !hasProgressed) {
                    // For lineage tab, make sure we're not detecting a false positive
                    // Wait a bit to ensure the modal is actually open and the tab shows X
                    if (targetSelector.includes('lineage-tab')) {
                        // Double-check that the tab actually shows the X icon
                        const tabContent = target.innerHTML || '';
                        const innerDiv = target.querySelector('div > div');
                        const actuallyHasX = tabContent.includes('✕') || (innerDiv && innerDiv.innerHTML.includes('✕'));
                        
                        if (!actuallyHasX) {
                            // Tab doesn't show X yet, wait a bit more
                            return;
                        }
                    }
                    
                    hasProgressed = true;
                    // Disable the tab immediately
                    target.classList.add('walkthrough-disabled');
                    target.style.opacity = '0.5';
                    target.style.pointerEvents = 'none';
                    target.style.cursor = 'not-allowed';
                    
                    // Progress to next step after a short delay
                    setTimeout(() => {
                        if (currentStep < walkthroughSteps.length - 1) {
                            setCurrentStep(currentStep + 1);
                        } else {
                            handleComplete();
                        }
                    }, 500);
                }
            };
            
            // Check immediately and then periodically
            checkModalOpen();
            const interval = setInterval(checkModalOpen, 200);
            
            return () => {
                clearInterval(interval);
                // Clean up disabled state when step changes
                if (target) {
                    target.classList.remove('walkthrough-disabled');
                    target.style.opacity = '';
                    target.style.pointerEvents = '';
                    target.style.cursor = '';
                }
            };
        }
    }, [currentStep, internalIsActive, hasInitializedRef.current, walkthroughSteps, location.pathname]);

    // Handle step processing
    useEffect(() => {
        if (!internalIsActive) {
            return;
        }

        // Only process steps when isActive is true
        const step = walkthroughSteps[currentStep];
        if (!step) {
            handleComplete();
            return;
        }

        // Step 4: Only skip step 6 if fork was actually completed (dedicated effect handles that case)
        // Otherwise, process step 6 normally as a modal
        if (currentStep === 6 && forkCompleted) {
            return;
        }

        // Guard: Don't re-process the same step if we're already highlighting the correct element
        // This prevents infinite loops when highlightedElement changes
        if (step.type === 'highlight' && lastProcessedStepRef.current === currentStep && highlightedElement) {
            const currentElement = document.querySelector(step.targetSelector);
            if (currentElement === highlightedElement) {
                // Already highlighting the correct element for this step, skip re-processing
                return;
            }
        }
        lastProcessedStepRef.current = currentStep;

        if (step.type === 'modal') {
            setIsModalOpen(true);
            setHighlightedElement(null);
            setOverlayMessage('');
            setOverlaySubMessage('');
            clearWalkthroughChef(); // Clear walkthrough chef when showing modal
            
            // Clear displayText - WalkthroughModal will handle its own typewriter effect
            setDisplayText('');
            setIsTyping(false);
            
            // Clear any existing interval
            if (textIntervalRef.current) {
                clearInterval(textIntervalRef.current);
                textIntervalRef.current = null;
            }
                } else if (step.type === 'highlight') {
                    // Don't close modal if we're on step 6 (finish modal)
                    if (currentStep !== 6) {
                        setIsModalOpen(false);
                    }
                    
                    // Skip step 5 (fork-submit) if we're on a different recipe page than when step 5 started
                    // This means the fork was created and we navigated away
                    if (step.targetSelector && step.targetSelector.includes('fork-submit')) {
                        const stepStartPath = stepStartPathnameRef.current;
                        const currentPath = location.pathname;
                        
                        // If pathname changed and we're on a recipe page, fork was created
                        if (stepStartPath && currentPath !== stepStartPath && 
                            currentPath.startsWith('/recipes/') && currentPath !== '/recipes') {
                            // Don't try to highlight - the fork creation watcher will handle progression
                            return;
                        }
                    }
                    
                    // Clean up any previous disabled/grayed-out state
                    const prevTargets = document.querySelectorAll('.walkthrough-disabled, .walkthrough-grayed-out');
                    prevTargets.forEach(prevTarget => {
                        prevTarget.classList.remove('walkthrough-disabled', 'walkthrough-grayed-out');
                        const tabElement = prevTarget.closest('.floating-tab-container') || prevTarget;
                        tabElement.style.opacity = '';
                        tabElement.style.pointerEvents = '';
                        tabElement.style.cursor = '';
                    });
                    
                    // Also clean up all grayed-out tabs
                    const allGrayedTabs = document.querySelectorAll('.walkthrough-grayed-out');
                    allGrayedTabs.forEach(tab => {
                        tab.classList.remove('walkthrough-grayed-out');
                        tab.style.opacity = '';
                        tab.style.pointerEvents = '';
                        tab.style.cursor = '';
                    });
                    
                    // Check if we should skip this step (e.g., if lineage is already open)
                    if (step.skipIfLineageOpen) {
                        // Check if we're already on a recipe details page with lineage open
                        const isOnRecipePage = location.pathname.startsWith('/recipes/');
                        const lineageViewer = document.querySelector('.lineage-viewer-container');
                        
                        // If lineage viewer is already visible, skip to next step
                        if (isOnRecipePage && lineageViewer && lineageViewer.offsetParent !== null) {
                            setTimeout(() => {
                                setCurrentStep(currentStep + 1);
                            }, 500);
                            return;
                        }
                    }
                    
                    // Navigate if needed
                    if (step.navigateTo) {
                        navigate(step.navigateTo);
                    }

            // Wait for page if needed
            if (step.waitForPage) {
                const checkPage = setInterval(() => {
                    if (location.pathname === step.waitForPage || location.pathname.startsWith('/recipes/')) {
                        clearInterval(checkPage);
                        setTimeout(() => {
                            highlightElement(step);
                        }, step.waitForPage === '/recipes' ? 1500 : 1000);
                    }
                }, 100);
                return () => clearInterval(checkPage);
            } else {
                // Small delay to ensure DOM is ready
                setTimeout(() => {
                    highlightElement(step);
                }, 200);
            }
        }

        return () => {
            if (textIntervalRef.current) {
                clearInterval(textIntervalRef.current);
                textIntervalRef.current = null;
            }
        };
    }, [currentStep, location.pathname, internalIsActive, navigate, setWalkthroughChef, walkthroughSteps, handleNext, handleComplete]);

    const highlightElement = (step) => {
        const element = document.querySelector(step.targetSelector);
        if (element) {
            setHighlightedElement(element);
            setOverlayMessage(step.message);
            setOverlaySubMessage(step.subMessage);
            setChefState(step.chefState || 'talking');
            // Use the existing FloatingChef via context
            setWalkthroughChef(
                step.message,
                step.subMessage,
                step.chefState || 'talking',
                step.position || 'right',
                step.showNextButton || false,
                step.showNextButton ? handleNext : null
            );
            
            // Gray out all other tabs except the highlighted one (only if highlighting a tab)
            const isTabElement = element.classList.contains('floating-tab-container') ||
                                (element.hasAttribute('data-walkthrough') && 
                                 element.getAttribute('data-walkthrough').includes('-tab'));
            
            if (isTabElement) {
                // Function to gray out tabs - call it immediately and after a delay
                const grayOutOtherTabs = () => {
                    const allTabs = document.querySelectorAll('.floating-tab-container');
                    const highlightedTab = element;
                    
                    allTabs.forEach(tab => {
                        // Don't gray out the highlighted tab
                        if (tab !== highlightedTab) {
                            tab.classList.add('walkthrough-grayed-out');
                            tab.style.opacity = '0.3';
                            tab.style.pointerEvents = 'none';
                            tab.style.cursor = 'not-allowed';
                        }
                    });
                };
                
                // Try immediately
                grayOutOtherTabs();
                // Try again after a delay in case tabs are conditionally rendered
                setTimeout(grayOutOtherTabs, 500);
            }
            
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
            // Use MutationObserver to watch for the element to appear
            let observer = null;
            let retryTimeout = null;
            let skipTimeout = null;
            
            const checkForElement = () => {
                const foundElement = document.querySelector(step.targetSelector);
                if (foundElement) {
                    // Clean up observers
                    if (observer) {
                        observer.disconnect();
                        observer = null;
                    }
                    if (retryTimeout) {
                        clearTimeout(retryTimeout);
                        retryTimeout = null;
                    }
                    if (skipTimeout) {
                        clearTimeout(skipTimeout);
                        skipTimeout = null;
                    }
                    
                    // Highlight the element
                    setHighlightedElement(foundElement);
                    setOverlayMessage(step.message);
                    setOverlaySubMessage(step.subMessage);
                    setChefState(step.chefState || 'talking');
                    setWalkthroughChef(
                        step.message,
                        step.subMessage,
                        step.chefState || 'talking',
                        step.position || 'right',
                        step.showNextButton || false,
                        step.showNextButton ? handleNext : null
                    );
                    
                    // Gray out tabs if needed
                    const isTabElement = foundElement.classList.contains('floating-tab-container') ||
                                        (foundElement.hasAttribute('data-walkthrough') && 
                                         foundElement.getAttribute('data-walkthrough').includes('-tab'));
                    
                    if (isTabElement) {
                        const allTabs = document.querySelectorAll('.floating-tab-container');
                        allTabs.forEach(tab => {
                            if (tab !== foundElement) {
                                tab.classList.add('walkthrough-grayed-out');
                                tab.style.opacity = '0.3';
                                tab.style.pointerEvents = 'none';
                                tab.style.cursor = 'not-allowed';
                            }
                        });
                    }
                    
                    foundElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    return true;
                }
                return false;
            };
            
            // Try immediately
            if (checkForElement()) {
                return;
            }
            
            // Set up MutationObserver to watch for element appearance
            if (window.MutationObserver) {
                observer = new MutationObserver(() => {
                    if (checkForElement()) {
                        // Element found, observer will be cleaned up in checkForElement
                    }
                });
                
                // Observe the entire document for changes
                observer.observe(document.body, {
                    childList: true,
                    subtree: true,
                    attributes: true,
                    attributeFilter: ['data-walkthrough', 'class']
                });
            }
            
            // Also set up periodic retries as a fallback
            retryTimeout = setInterval(() => {
                if (checkForElement()) {
                    // Element found, interval will be cleared in checkForElement
                }
            }, 500);
            
            // Set up a timeout to skip after 10 seconds if element still not found
            skipTimeout = setTimeout(() => {
                if (observer) {
                    observer.disconnect();
                }
                if (retryTimeout) {
                    clearInterval(retryTimeout);
                }
                if (currentStep < walkthroughSteps.length - 1) {
                    setCurrentStep(currentStep + 1);
                }
            }, 10000);
            
            // Return cleanup function
            return () => {
                if (observer) {
                    observer.disconnect();
                }
                if (retryTimeout) {
                    clearInterval(retryTimeout);
                }
                if (skipTimeout) {
                    clearTimeout(skipTimeout);
                }
            };
        }
    };

    const handleStart = () => {
        // Clear any typing interval
        if (textIntervalRef.current) {
            clearInterval(textIntervalRef.current);
            textIntervalRef.current = null;
        }
        setIsTyping(false);
        setDisplayText('');
        // Close modal first
        setIsModalOpen(false);
        // Force state update and move to next step
        // Use a small delay to ensure modal closes before processing next step
        setTimeout(() => {
            setCurrentStep(1);
        }, 150);
    };

    const handleElementClick = (e) => {
        const step = walkthroughSteps[currentStep];
        if (!step) return;
        
        // Check if clicked element matches target
        const target = document.querySelector(step.targetSelector);
        if (!target) {
            return;
        }
        
        // Check if the click is on the target or any of its children
        const clickedElement = e.target;
        const isClickOnTarget = target === clickedElement || 
                                target.contains(clickedElement) ||
                                clickedElement.closest(step.targetSelector) === target;
        
        if (isClickOnTarget) {
            // Don't progress if this step requires specific interaction (like input)
            if (step.waitForInteraction && step.interactionType === 'input') {
                // Wait for input change instead
                return;
            }
            
            // If this is a tab step, disable it immediately and let modal watcher handle progression
            const isTabStep = step.targetSelector.includes('lineage-tab') || 
                             step.targetSelector.includes('fork-tab') || 
                             step.targetSelector.includes('compare-tab');
            
            if (isTabStep) {
                // Disable the tab immediately
                target.classList.add('walkthrough-disabled');
                target.style.opacity = '0.5';
                target.style.pointerEvents = 'none';
                target.style.cursor = 'not-allowed';
                
                // For tab steps, wait for modal to open (handled by the modal watcher)
                // Don't progress here - let the modal watcher handle it
                return;
            }
            
            // Special handling for fork-submit: let the modal watcher detect navigation
            if (step.targetSelector.includes('fork-submit')) {
                // Don't progress immediately - let the modal watcher detect when fork is created
                // and navigation happens. The modal watcher will detect when the modal closes
                // and we're on a recipe page, then progress to the next step.
                return;
            }
            
            // For steps with waitForInteraction, wait a bit before progressing
            // For other steps, progress immediately
            const delay = step.waitForInteraction ? 500 : 200;
            setTimeout(() => {
                handleNext();
            }, delay);
        }
    };

    // Ensure tabs are grayed out when element is highlighted
    useEffect(() => {
        if (!highlightedElement || !isActive) return;
        
        const step = walkthroughSteps[currentStep];
        if (!step || step.type !== 'highlight') return;
        
        // Check if highlighting a tab
        const isTabElement = highlightedElement.classList.contains('floating-tab-container') ||
                            (highlightedElement.hasAttribute('data-walkthrough') && 
                             highlightedElement.getAttribute('data-walkthrough').includes('-tab'));
        
        if (isTabElement) {
            // Use a small delay to ensure all tabs are rendered (some are conditionally rendered)
            const grayOutTabs = () => {
                const allTabs = document.querySelectorAll('.floating-tab-container');
                
                allTabs.forEach(tab => {
                    if (tab !== highlightedElement) {
                        tab.classList.add('walkthrough-grayed-out');
                        tab.style.opacity = '0.3';
                        tab.style.pointerEvents = 'none';
                        tab.style.cursor = 'not-allowed';
                    }
                });
            };
            
            // Try immediately and then again after delays
            grayOutTabs();
            const timeout1 = setTimeout(grayOutTabs, 200);
            const timeout2 = setTimeout(grayOutTabs, 500);
            const timeout3 = setTimeout(grayOutTabs, 1000);
            
            // Use MutationObserver to watch for new tabs being added to the DOM
            const observer = new MutationObserver(() => {
                grayOutTabs();
            });
            
            // Observe the document body for new tab additions
            observer.observe(document.body, {
                childList: true,
                subtree: true
            });
            
            return () => {
                clearTimeout(timeout1);
                clearTimeout(timeout2);
                clearTimeout(timeout3);
                observer.disconnect();
                // Clean up when element changes
                const allTabs = document.querySelectorAll('.walkthrough-grayed-out');
                allTabs.forEach(tab => {
                    tab.classList.remove('walkthrough-grayed-out');
                    tab.style.opacity = '';
                    tab.style.pointerEvents = '';
                    tab.style.cursor = '';
                });
            };
        }
    }, [highlightedElement, currentStep, internalIsActive]);

    useEffect(() => {
        if (highlightedElement && !isModalOpen) {
            // Use capture phase to catch clicks before they bubble
            highlightedElement.addEventListener('click', handleElementClick, true);
            return () => {
                highlightedElement.removeEventListener('click', handleElementClick, true);
            };
        }
    }, [highlightedElement, currentStep, isModalOpen]);

    if (!internalIsActive) return null;

    const currentStepData = walkthroughSteps[currentStep];
    const isEndStep = currentStep === walkthroughSteps.length - 1 && currentStepData.type === 'modal';

    return (
        <>
            {isModalOpen && currentStepData && currentStepData.type === 'modal' && (
                <WalkthroughModal
                    isOpen={isModalOpen}
                    onClose={() => {
                        // Track skip when X is clicked
                        handleSkip();
                    }}
                    onStart={currentStep === 0 ? handleStart : undefined}
                    title={currentStepData.title}
                    message={currentStepData.message}
                    subMessage={currentStepData.subMessage}
                    chefState={currentStepData.chefState || 'talking'}
                    showStartButton={currentStep === 0}
                    startButtonText={currentStepData.startButtonText}
                    showImage={currentStepData.showImage}
                    imageUrl={currentStepData.imageUrl}
                    showEndButton={isEndStep}
                    endButtonText={currentStepData.endButtonText || "Got it!"}
                    onEnd={isEndStep ? handleComplete : undefined}
                    showSecondButton={currentStepData.showSecondButton || false}
                    secondButtonText={currentStepData.secondButtonText || ""}
                    onSecondButton={currentStepData.showSecondButton ? handleStartCompareWalkthrough : undefined}
                />
            )}
            
            {highlightedElement && !isModalOpen && (
                <WalkthroughOverlay
                    isActive={true}
                    highlightedElement={highlightedElement}
                    message=""
                    subMessage=""
                    position={currentStepData?.position || 'right'}
                />
            )}
        </>
    );
};

export default LineageWalkthroughManager;

